/**
 * "Start KPI Investigation" dialog — app-level, mounted once beside
 * KoryCellContextMenu. Opens from the cell menu's high/low submenu (the
 * provider's ``kpiInvestigationDraft``); the user picks a category, optionally
 * adds detail, and the investigation is created + owner-routed server-side.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowDownRight, ArrowUpRight, Loader2 } from "lucide-react";
import { useCallback, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useKory } from "@/contexts/kory-context";
import { resolvePageOwner } from "@/lib/nav-v2/catalog";
import { cn } from "@/lib/utils";
import {
  CATEGORY_OPTIONS,
  createKpiInvestigation,
  type KpiInvestigationCreateCategory,
  type KpiInvestigationCreateInput,
} from "@/services/kpi-investigation-services";

export function KpiInvestigationDialog() {
  const { kpiInvestigationDraft, closeKpiInvestigation } = useKory();
  const queryClient = useQueryClient();

  const [category, setCategory] = useState<KpiInvestigationCreateCategory | null>(null);
  const [detail, setDetail] = useState("");

  const open = kpiInvestigationDraft != null;
  const cell = kpiInvestigationDraft?.cell;
  const direction = kpiInvestigationDraft?.direction;

  const handleClose = useCallback(() => {
    setCategory(null);
    setDetail("");
    closeKpiInvestigation();
  }, [closeKpiInvestigation]);

  const { mutate: submit, isPending } = useMutation({
    mutationFn: (payload: KpiInvestigationCreateInput) => createKpiInvestigation(payload),
    onSuccess: (created) => {
      toast.success(
        created.assignee_name
          ? `KPI investigation opened — routed to ${created.assignee_name}`
          : "KPI investigation opened (unassigned — no owner configured)",
      );
      void queryClient.invalidateQueries({ queryKey: ["kpi-investigations"] });
      handleClose();
    },
    onError: (e: Error) => {
      toast.error(e.message || "Could not open the investigation — please try again.");
    },
  });

  const handleSubmit = () => {
    if (!cell?.metricSlug || !direction || !category) return;
    // One-line description of the figure under investigation, composed from the
    // cell so the record is readable without the original page.
    const valueSummary = [
      `${cell.metricName} (${cell.columnLabel}) = ${cell.displayValue}`,
      cell.brand,
      cell.periodLabel ?? cell.periodId,
    ]
      .filter(Boolean)
      .join(" · ");
    submit({
      kpi_slug: cell.metricSlug,
      category,
      direction,
      metric_name: cell.metricName,
      brand: cell.brand,
      value_summary: valueSummary,
      // The menu cell never passed through attachCell (where pageKey is
      // normally stamped), so resolve the page-owner key here. A cell bridged
      // from an ?embed=1 iframe arrives pre-stamped with ITS page's key —
      // prefer that over this window's route (the embedding page, e.g. the
      // meeting, not the dashboard the cell lives on).
      page_key: cell.pageKey ?? resolvePageOwner(window.location.pathname) ?? undefined,
      ...(detail.trim() ? { detail: detail.trim() } : {}),
    });
  };

  const DirectionIcon = direction === "low" ? ArrowDownRight : ArrowUpRight;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <DialogContent className="max-w-md gap-0 p-0">
        <DialogHeader className="border-b px-5 pb-3 pt-4">
          <DialogTitle className="text-base font-bold">Start KPI Investigation</DialogTitle>
          {cell && (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
              <DirectionIcon className="h-3.5 w-3.5" aria-hidden />
              {cell.metricName} = {cell.displayValue}
              {cell.brand ? ` · ${cell.brand}` : ""}
              {" · seems "}
              {direction}
            </p>
          )}
        </DialogHeader>

        <div className="flex flex-col gap-4 px-5 py-4">
          {/* Category — single-select stacked options (no radio-group primitive
              in this codebase; aria-pressed buttons, like the time-off dialog's
              SegmentControl, but vertical with descriptions). */}
          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              What seems off?
            </span>
            <div className="flex flex-col gap-1" role="group" aria-label="Investigation category">
              {CATEGORY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  aria-pressed={category === opt.value}
                  onClick={() => setCategory(opt.value)}
                  className={cn(
                    "rounded-md border px-3 py-2 text-left transition-colors",
                    category === opt.value
                      ? "border-primary bg-primary/5"
                      : "border-input hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  <span className="block text-sm font-medium">{opt.label}</span>
                  <span className="block text-xs text-muted-foreground">{opt.description}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Detail */}
          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Detail <span className="font-normal normal-case">(optional)</span>
            </span>
            <Textarea
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              placeholder="Anything the owner should know…"
              className="resize-none text-sm"
              rows={2}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t px-5 py-3">
          <Button variant="outline" size="sm" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button size="sm" disabled={!category || isPending} aria-busy={isPending} onClick={handleSubmit}>
            {isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Open Investigation
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
